import { useQuery, useMutation } from "convex/react";
import { api } from "@convex/_generated/api";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router";
import type { Id } from "@convex/_generated/dataModel";
import {
  ApplicationEnum,
  estLienDriveNonExploitable,
  normaliserUrlLogo,
  VERSION_COPSOQ3_PAR_DEFAUT,
  type ApplicationConfig,
  type VersionCopsoq3Enum,
} from "@repo/utils";
import { Breadcrumb, Button, Input, Label, PageContainer } from "@repo/ui";
import { ChoixApplications } from "../../components/ChoixApplications";
import { ChoixVersionCopsoq3 } from "../../components/ChoixVersionCopsoq3";
import { arianeOrganisation, libelleSection } from "../../lib/ariane";

export function OrganisationForm() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const existing = useQuery(
    api.superAdmin.obtenirOrganisation,
    isEdit ? { organisationId: id as Id<"organisations"> } : "skip",
  );

  const creer = useMutation(api.superAdmin.creerOrganisation);
  const modifier = useMutation(api.superAdmin.modifierOrganisation);

  const [nom, setNom] = useState("");
  const [applications, setApplications] = useState<ApplicationConfig["cle"][]>(
    [],
  );
  const [versionCopsoq3, setVersionCopsoq3] = useState<VersionCopsoq3Enum>(
    VERSION_COPSOQ3_PAR_DEFAUT,
  );
  const [logoUrl, setLogoUrl] = useState("");
  const [credits, setCredits] = useState("0");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setNom(existing.nom);
      setApplications(existing.applicationsAutorisees);
      setVersionCopsoq3(existing.versionCopsoq3);
      setLogoUrl(existing.logoUrl ?? "");
    }
  }, [existing]);

  // Dérivées du champ de saisie : calculées au render, pas stockées.
  const logoUrlNormalisee = normaliserUrlLogo(logoUrl);
  const lienDriveIllisible = estLienDriveNonExploitable(logoUrl);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // Solde initial, création seulement : ensuite le solde s'ajuste depuis
      // la page Crédits, qui trace chaque variation. Peut être NÉGATIF : le
      // solde à découvert est la trace de ce qui a été consommé sans achat.
      const creditsNombre = Number(credits);
      if (!Number.isInteger(creditsNombre)) {
        throw new Error("Le nombre de crédits doit être un entier");
      }

      if (isEdit) {
        await modifier({
          organisationId: id as Id<"organisations">,
          nom,
          applicationsAutorisees: applications,
          versionCopsoq3,
          logoUrl,
        });
      } else {
        await creer({
          nom,
          applicationsAutorisees: applications,
          versionCopsoq3,
          logoUrl,
          credits: creditsNombre,
        });
      }
      void navigate("/");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && existing === undefined) {
    return (
      <div className="flex justify-center p-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <PageContainer>
      <Breadcrumb
        items={
          isEdit
            ? arianeOrganisation(id, existing?.nom, "modifier")
            : [{ label: "Organisations", href: "/" }, { label: "Nouvelle organisation" }]
        }
      />
      <h1 className="text-2xl font-bold mb-6 text-center">
        {isEdit ? libelleSection("modifier") : "Nouvelle organisation"}
      </h1>

      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-card border border-border rounded-surface p-6 max-w-xl mx-auto"
      >
        <div>
          <Label htmlFor="org-nom" className="mb-1">
            Nom *
          </Label>
          <Input
            id="org-nom"
            value={nom}
            onChange={(e) => setNom(e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="org-logo" className="mb-1">
            URL du logo
          </Label>
          <Input
            id="org-logo"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://drive.google.com/file/d/…/view"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Déposez le logo sur Drive, partagez-le en « tous les utilisateurs
            disposant du lien », puis collez l'adresse ici. Le lien de partage
            est réécrit automatiquement en lien d'image.
          </p>
          {logoUrlNormalisee && (
            <div className="mt-3 flex items-center gap-3 rounded-surface border border-border p-3">
              {/* Preview servie par l'URL RÉELLEMENT enregistrée : si elle ne
                  s'affiche pas ici, elle ne s'affichera pas côté client. */}
              <img
                src={logoUrlNormalisee}
                alt="Aperçu du logo"
                // Même parade que côté portail client : sans `no-referrer`,
                // Drive renvoie 429 sur un Referer localhost et l'aperçu reste
                // vide alors que l'URL est bonne.
                referrerPolicy="no-referrer"
                className="max-h-12 w-auto max-w-[10rem] object-contain"
              />
              <code className="text-xs text-muted-foreground break-all">
                {logoUrlNormalisee}
              </code>
            </div>
          )}
          {lienDriveIllisible && (
            <p className="text-xs text-destructive mt-1">
              Ce lien Google Drive ne contient pas d'identifiant de fichier
              exploitable : l'image ne s'affichera pas.
            </p>
          )}
        </div>

        {isEdit ? (
          <div>
            <p className="text-sm font-medium mb-1">Crédits</p>
            <p className="text-sm">
              Solde actuel : <strong>{existing?.credits ?? 0}</strong> ·{" "}
              <Link
                to={`/organisations/${id}/credits`}
                className="text-primary hover:underline"
              >
                Ajuster et voir l'historique
              </Link>
            </p>
          </div>
        ) : (
        <div>
          <Label htmlFor="org-credits" className="mb-1">
            Crédits initiaux
          </Label>
          <Input
            id="org-credits"
            type="number"
            step={1}
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Affiché en lecture seule dans le portail client. Peut être négatif :
            un solde à découvert mesure ce qui a été consommé sans achat.
          </p>
        </div>
        )}

        {/* `fieldset`/`legend` plutôt qu'un `<label>` : un label doit désigner UN
            champ, or il s'agit ici d'un groupe de cases. */}
        <fieldset>
          <legend className="block text-sm font-medium">
            Applications autorisées *
          </legend>
          <p className="text-xs text-muted-foreground mt-1">
            Les épreuves que l'organisation peut proposer dans ses campagnes.
            Elle les combine librement à la création de chaque campagne.
          </p>
          <ChoixApplications
            idPrefix="application"
            value={applications}
            onChange={setApplications}
            reglages={{
              [ApplicationEnum.COPSOQ3]: (
                <ChoixVersionCopsoq3
                  value={versionCopsoq3}
                  onChange={setVersionCopsoq3}
                />
              ),
            }}
          />
        </fieldset>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Annuler
          </Button>
          <Button type="submit" disabled={saving || applications.length === 0}>
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
